import { CartHelper } from "./CartHelper.js"
import { ProductConfigurator } from "./ProductConfigurator.js"
import { I18N } from "./I18N.js"
import { Spinner } from "./Spinner.js"
import { ProductHelper } from "./ProductHelper.js"
import { Image } from "./lib/Image.js"

export class Products {
	_target
	_onAddToCartHandlers = []
	_categories
	_extraCategories
	_options
	_categoriesToExtraCategories
	_productsToCategories
	_productsToExtraOptions
	_extraCategoriesToExtraOptions
	_products
	_taxRates
	_categoryMedia = {}
	_categoryTranslations = {}
	_productMedia = {}
	_productTranslations = {}
	_store
	_getProductsPromise
	_productConfigurator
	_hideTax = true
	_hideNet = true

	constructor(target) {
		this._target = document.getElementById(target)
		this._target.setAttribute('hidden', '')
		this._target.innerHTML = `
			<div id="storeHeader"></div>
			<div id="storeProducts"></div>
			<div id="productConfigurator"></div>
		`
		I18N.translateElement(this._target)

		this._loadProducts() // Makes this._getProductsPromise
		// Check every 30 minutes if product or option validUntil expires soon
		setInterval(() => {
			if (this._productsOrOptionsExpire(4)) {
				console.log('Reloading products and options because some signatures will expire soon...')
				this._loadProducts()
					.then(() => console.log('Products refreshed.'))
			}
		}, 30 * 60 * 1000)
	}

	addOnAddToCartHandlers = (...onAddToCartHandlers) => this._onAddToCartHandlers.push(...onAddToCartHandlers)

	setStore = store => {
		if (!this._store || this._store.id !== store.id) {
			this._store = store
			this._getProductsPromise
				.then(() => this._displayProducts())
			if (this._productConfigurator) {
				this._productConfigurator.close()
				this._productConfigurator = null
			}
		}
	}

	setVisible = visible => visible ? this._target.removeAttribute('hidden') : this._target.setAttribute('hidden', '')

	_loadProducts = () => {
		const storePromises = []
		storePromises.push(fetch('/products/taxRates', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/categories', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/extraCategories', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/options', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/categoriesToExtraCategories', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/productsToCategories', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/productsToExtraOptions', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/extraCategoriesToExtraOptions', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/products', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/categoryMedia', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/productMedia', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/categoryTranslations', {
			method: 'GET'
		}))
		storePromises.push(fetch('/products/productTranslations', {
			method: 'GET'
		}))

		this._getProductsPromise = new Promise((resolve, reject) => {
			const spinner = new Spinner(this._target, 15)
			Promise.all(storePromises)
				.then(([taxRatesResp, categoriesResp, extraCategoriesResp, optionsResp, categoriesToExtraCategoriesResp, productsToCategoriesResp, productsToExtraOptionsResp, extraCategoriesToExtraOptionsResp, productsResp, categoryMediaResp, productMediaResp, categoryTranslationsResp, productTranslationsResp]) => {
					const jsonPromises = []
					jsonPromises.push(taxRatesResp.json())
					jsonPromises.push(categoriesResp.json())
					jsonPromises.push(extraCategoriesResp.json())
					jsonPromises.push(optionsResp.json())
					jsonPromises.push(categoriesToExtraCategoriesResp.json())
					jsonPromises.push(productsToCategoriesResp.json())
					jsonPromises.push(productsToExtraOptionsResp.json())
					jsonPromises.push(extraCategoriesToExtraOptionsResp.json())
					jsonPromises.push(productsResp.json())
					jsonPromises.push(categoryMediaResp.json())
					jsonPromises.push(productMediaResp.json())
					jsonPromises.push(categoryTranslationsResp.json())
					jsonPromises.push(productTranslationsResp.json())
					Promise.all(jsonPromises)
						.then(([taxRates, categories, extraCategories, options, categoriesToExtraCategories, productsToCategories, productsToExtraOptions, extraCategoriesToExtraOptions, products, categoryMedia, productMedia, categoryTranslations, productTranslations]) => {
							this._taxRates = taxRates
							this._categories = categories
							this._extraCategories = extraCategories
							this._options = options
							this._categoriesToExtraCategories = categoriesToExtraCategories
							this._productsToCategories = productsToCategories
							this._productsToExtraOptions = productsToExtraOptions
							this._extraCategoriesToExtraOptions = extraCategoriesToExtraOptions
							this._products = products
							this._categoryMedia = categoryMedia || {}
							this._productMedia = productMedia || {}
							this._categoryTranslations = categoryTranslations || {}
							this._productTranslations = productTranslations || {}
							resolve()
							spinner.dismiss()
						})
				})
				.catch(e => {
					console.error('Failed to load products:', e)
					spinner.dismiss()
					reject(e)
				})
		})
		return this._getProductsPromise
	}

	_productsOrOptionsExpire = (minValidTimeHours) => {
		const reqiredValidTime = new Date().getTime() + minValidTimeHours * 60 * 60 * 1000
		return this._products.some(p => !(p.validUntil >= reqiredValidTime)) || this._options.some(o => !(o.validUntil >= reqiredValidTime))
	}

	_getEnrichedProduct = product => {
		const translations = this._productTranslations[product.productId] || []
		const locale = (navigator.language || 'de').split('-')[0]
		const t = translations.find(t => t.locale === locale)
			|| translations.find(t => t.locale === 'de')
			|| translations[0]
		if (!t) return product
		const md = new window.showdown.Converter()
		return {
			...product,
			translatedName:      t.name || null,
			shortDescriptionHtml: t.short_description ? md.makeHtml(t.short_description) : null,
			descriptionHtml:      t.description       ? md.makeHtml(t.description)       : null,
		}
	}

	_selectProduct = product => {
		console.debug('Selected Product: ', product)
		this._productConfigurator = new ProductConfigurator('productConfigurator', this, this._getEnrichedProduct(product), this._store, this._taxRates.find(taxRate => taxRate.id === product.taxRateId))
		this._productConfigurator.addOnAddToCartHandlers(...this._onAddToCartHandlers)
		this._productConfigurator.setVisible(true)
	}

	_addProductToCart = product => {
		console.log('Selected Product: ', product)
		this._productConfigurator = new ProductConfigurator('productConfigurator', this, this._getEnrichedProduct(product), this._store, this._taxRates.find(taxRate => taxRate.id === product.taxRateId))
		this._productConfigurator.addOnAddToCartHandlers(...this._onAddToCartHandlers)
		if (!ProductHelper.requiresVariantSelection(product) && !this._productConfigurator.getMissingRequiredExtraCategories().length) {
			this._productConfigurator.addToCart()
			this._productConfigurator = null
		}
		else {
			this._productConfigurator.setVisible(true)
		}
	}

	_displayProducts = () => {
		let allProducts = []
		const browserLocale = (navigator.language || 'de').split('-')[0]
		const mdConverter = new window.showdown.Converter()

		const getCategoryShortDescription = (categoryId) => {
			const translations = this._categoryTranslations[categoryId] || []
			const t = translations.find(t => t.locale === browserLocale)
				|| translations.find(t => t.locale === 'de')
				|| translations[0]
			if (!t?.short_description) return ''
			return `<div class="categoryShortDescription">${mdConverter.makeHtml(t.short_description)}</div>`
		}

		const getProductTranslation = (productId) => {
			const translations = this._productTranslations[productId] || []
			return translations.find(t => t.locale === browserLocale)
				|| translations.find(t => t.locale === 'de')
				|| translations[0]
				|| null
		}

		const getCategoryBgImg = (categoryId) => {
			const mediaList = this._categoryMedia[categoryId] || []
			const first = mediaList.find(m => m.type === 'image')
			if (!first) return ''
			return `<img src="data:${first.mime_type};base64,${first.data}" alt="${first.description || ''}">`
		}

		const getSubCategories = (parentId) => {
			let buffer = ''
			this._categories
				.filter(c => c.parentCategoryId === parentId)
				.filter(c => this._productsToCategories.some(pc => pc.categoryId === c.categoryId))
				.forEach(c => {
				buffer += `<tr categoryId="${c.categoryId}">
						<td colspan="6" class="productCategory">
							<div class="categoryHeader">
								${getCategoryBgImg(c.categoryId)}
								<div class="badge">${c.name.replaceAll('_', ' ')}</div>
							</div>
							${getCategoryShortDescription(c.categoryId)}
						</td>
					</tr>`
				buffer += getProductRowsHtml(c.categoryId)
			})
			return buffer
		}

		const getCategories = () => {
			const mainCategory = this._categories.find(c => c.categoryId === this._store.rootProductCategoryId)
			if (mainCategory) {
				this._displayStoreHeader(mainCategory)
				return getSubCategories(mainCategory.categoryId)
			}
			else {
				console.warn('No main category for store: ', this._store.name)
				return ''
			}
		}

		const getPriceCellContent = (product, extractFunction) => {
			// Net price and range
			let simpleString = null
			let rangeString = null
			if (ProductHelper.hasVariants(product)) {
				const defaultVariant = product.variants.find(v => v.default)
				if (defaultVariant) {
					simpleString = CartHelper.formatPrice(extractFunction(defaultVariant))
				}
				const minValue = Math.min(...product.variants.map(v => extractFunction(v)))
				const maxValue = Math.max(...product.variants.map(v => extractFunction(v)))
				rangeString = I18N.translateAndFormat('Variants {0} - {1}', CartHelper.formatPrice(minValue), CartHelper.formatPrice(maxValue))
			}
			else {
				simpleString = CartHelper.formatPrice(extractFunction(product))
			}
			return !simpleString ? `<div class="productPrice range rangeOnly">${rangeString}</div>` : `<div class="productPrice">${simpleString}</div>${rangeString ? `<div class="productPrice range withRange">${rangeString}</div>` : ''}`
		}

		const getProductRowsHtml = categoryId => {
			const productIds = this._productsToCategories.filter(pc => pc.categoryId === categoryId).map(pc => pc.productId)
			const products = this._products
				.filter(p => productIds.includes(p.productId))
			allProducts = allProducts.concat(products)
			return products.map(p => {
				let requiresConfiguration = ProductHelper.requiresVariantSelection(p) || ProductHelper.requiresExtraOptionSelection(this, p.productId)
				const translation = getProductTranslation(p.productId)
				const displayName = translation?.name || p.productName
				const shortDesc = translation?.short_description
					? mdConverter.makeHtml(translation.short_description)
					: (p.shortDescription ? p.shortDescription.replace(/<\/?p>/g, '') : '')
				const mediaList = this._productMedia[p.productId] || []
				const firstImage = mediaList.find(m => m.type === 'image')
				const productImg = firstImage
					? `<img src="data:${firstImage.mime_type};base64,${firstImage.data}" alt="${firstImage.description || displayName}" class="productIcon">`
					: ''
				return `<tr productId="${p.productId}" class="product" ${p.purchasable ? '' : 'disabled'}>
						<td class="wide-table-col productCol" totalSales="${p.totalSales}" menuOrder="${p.menuOrder}">
							<div>
								<h4>${displayName}</h4>
								<div class="productDescription">
									${productImg}
									${shortDesc ? `<div class="productDescriptionText">${shortDesc}</div>` : ''}
								</div>
							</div>
						</td>
						${this._hideNet ? '' : `<td class="priceColumn">${getPriceCellContent(p, q => q.net)}</td>`}
						${this._hideTax ? '' : `<td class="priceColumn">${getPriceCellContent(p, q => q.tax)}</td>`}
						<td class="priceColumn">${getPriceCellContent(p, q => q.net + q.tax)}</td>
						<td><button id="productSelect${p.productId}Btn" class="primary" title="${I18N.translate('Select Options and Extras')}" i18n>Select</button></td>
						<td><button id="productAddToCart${p.productId}Btn" class="primary" ${requiresConfiguration ? 'disabled' : ''}  title="${I18N.translate('Add to Cart ')}"i18n>Add to Cart</button></td>
					</tr>`
			}).join('')
		}

		const table = `<table class="priceTable productsTable">
			<!--<thead>
				<td></td>
				${this._hideNet ? '' : `<td class="priceColumn" i18n>Net</td>`}
				${this._hideTax ? '' : `<td class="priceColumn" i18n>Tax</td>`}
				<td class="priceColumn" i18n>Gross</td>
				<td></td>
			</thead>-->
			<tbody>
				${getCategories()}
			</tbody>
		</table>`

		document.getElementById('storeProducts').innerHTML = table
		I18N.translateElement(document.getElementById('storeProducts'))
		// Add select handlers
		allProducts.forEach(p => {
			const productAddToCartBtn = document.getElementById(`productAddToCart${p.productId}Btn`)
			if (productAddToCartBtn) {
				productAddToCartBtn.addEventListener('click', () => this._addProductToCart(p))
			}
			const productBtn = document.getElementById(`productSelect${p.productId}Btn`)
			if (productBtn) {
				productBtn.addEventListener('click', () => this._selectProduct(p))
			}
		})
	}

	_displayStoreHeader = mainCategory => {
		this._target.querySelector('#storeHeader').innerHTML = `
			${mainCategory.image && mainCategory.image.url
				? Image.insert(mainCategory.image.url, mainCategory.name, ['logo'], null, true)
				: `<h2 i18n>Products</h2>
				<div><span i18n>Store</span>: <strong>${mainCategory.name}</strong></div>`
			}
			${mainCategory.description
				? `<div>${new window.showdown.Converter().makeHtml(mainCategory.description)}</div>`
				: ''
			}
		`
	}

}